

import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../../context/AuthContext";
import api from "../../api/api";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import Spinner from "../../components/ui/Spinner";
import MessageBox from "../../components/ui/MessageBox";
import Button from "../../components/ui/Button";
import OrderSalesOrderItems from "./OrderSalesOrderItems";
import { XCircle, Printer, Send } from "lucide-react";

/**
 * PrintPreview component (self-contained here so SalesOrderNewItem works out of the box)
 * props:
 *  - htmlContent: string (HTML for preview)
 *  - onClose: fn
 *  - order: object (used for mobile number)
 */
const PrintPreview = ({ htmlContent, onClose, order }) => {
  const hiddenRef = useRef(null);
  const [waMenuOpen, setWaMenuOpen] = useState(false); // <-- added

  useEffect(() => {
    const handleKeyDown = async (e) => {
      if (e.key === "Escape") {
        onClose();
      } else if (e.key === "F8") {
        e.preventDefault();
        if (!hiddenRef.current) return;

        const mobileNumber = (order?.MobileNo || "").replace(/[^0-9]/g, "");
        if (!mobileNumber) return;

        const fullNumber = "91" + mobileNumber;

        try {
          const canvas = await html2canvas(hiddenRef.current, { scale: 2, useCORS: true });
          const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
          if (blob && navigator.clipboard && window.ClipboardItem) {
            await navigator.clipboard.write([new window.ClipboardItem({ "image/png": blob })]);
          }
          const message = encodeURIComponent("");
          window.open(`https://wa.me/${fullNumber}?text=${message}`, "_blank");
        } catch (err) {
          console.error("Failed to open WhatsApp:", err);
        }
      } else if (e.key === "F6") {
        e.preventDefault();
        handlePrint();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [order]);


  const handlePrint = () => {
    const iframe = document.getElementById('print-iframe');
    if (iframe) {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    }
  };

  const handleWhatsAppClick = () => {
    setWaMenuOpen((prev) => !prev);
  };

  const shareAsImage = async () => {
    setWaMenuOpen(false);
    if (!hiddenRef.current) return alert("Content not ready.");

    const countryCode = "91";
    const mobileNumber = order?.MobileNo?.replace(/[^0-9]/g, "") || "";
    if (!mobileNumber) return alert("Mobile number not available.");

    const fullNumber = countryCode + mobileNumber;

    try {
      const canvas = await html2canvas(hiddenRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: null,
      });
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
      if (blob) await navigator.clipboard.write([new window.ClipboardItem({ "image/png": blob })]);

      alert("Image copied! Paste in WhatsApp.");
      window.open(`https://wa.me/${fullNumber}`, "_blank");
    } catch (err) {
      console.warn(err);
    }
  };

  const shareAsPDF = async () => {
    setWaMenuOpen(false);
    if (!hiddenRef.current) return alert("Content not ready.");

    try {
      // Capture full content with higher resolution
      const canvas = await html2canvas(hiddenRef.current, {
        scale: 2,
        useCORS: true,
        scrollY: -window.scrollY,
        windowWidth: hiddenRef.current.scrollWidth, // ✅ ensure full width capture
        windowHeight: hiddenRef.current.scrollHeight,
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      // Calculate image size to fit full page width
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      // Add first page
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;

      // Add remaining pages
      while (heightLeft > 0) {
        position -= pdfHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight;
      }

      // Export or share
      const pdfBlob = pdf.output("blob");
      const file = new File([pdfBlob], "invoice.pdf", { type: "application/pdf" });

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "Invoice PDF",
          text: "Please find attached invoice PDF",
        });
      } else {
        const blobUrl = URL.createObjectURL(pdfBlob);
        window.open(blobUrl, "_blank");
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
      }
    } catch (err) {
      console.error("PDF generation failed:", err);
      alert("Failed to generate PDF. Please try again.");
    }
  };

  return (
    <>
      {/* Hidden content for HTML to Canvas */}
      <div
        ref={hiddenRef}
        style={{
          position: "fixed",
          top: "-9999px",
          left: "-9999px",
          width: "1000px", // ✅ wider for full invoice content
          background: "#fff",
          padding: "10px",
          transform: "scale(1)", // ensure no zoom clipping
          transformOrigin: "top left",
        }}
        dangerouslySetInnerHTML={{ __html: htmlContent }}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
        <div className="relative w-full max-w-2xl p-4 bg-white rounded-lg shadow-2xl">

          <h3 className="mb-2 text-lg font-semibold text-center">Preview</h3>

          <div className="border border-gray-300">
            <iframe
              id="print-iframe"
              srcDoc={htmlContent}
              title="Print Preview"
              style={{ width: '100%', height: '60vh', border: 'none' }}
            />
          </div>


          {/* Footer Buttons */}
          <div className="flex justify-end mt-2 gap-7">
            <Button
              onClick={onClose}
              className="flex items-center gap-2 px-2 py-1.5 text-sm font-medium text-white 
            bg-gradient-to-r from-red-500 to-red-600 rounded-lg shadow-md 
            hover:from-red-600 hover:to-red-700 hover:shadow-lg 
            transition-all duration-300 ease-in-out transform hover:scale-105"
            >
              <XCircle size={16} /> Close
            </Button>

            <Button
              onClick={handlePrint}
              className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white
               bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg shadow-md
               hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg
               transition-all duration-300 ease-in-out transform hover:scale-105"
            >
              <Printer size={16} /> Print (F6)
            </Button>

            {/* WhatsApp Button */}
            <div className="relative">
              <Button
                onClick={handleWhatsAppClick}
                className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white 
      bg-green-500 rounded-lg shadow-md hover:bg-green-600 transition-all duration-300"
              >
                <Send size={16} /> WhatsApp
              </Button>

              {waMenuOpen && (
                <div className="absolute right-0 z-50 mt-2 overflow-hidden bg-white border rounded-lg shadow-lg w-44">
                  <button
                    onClick={shareAsImage}
                    className="flex items-center w-full gap-2 px-3 py-2 text-sm font-medium text-white transition-all duration-200 bg-gradient-to-r from-green-400 to-green-500 hover:from-green-500 hover:to-green-600"
                  >
                    <img src="/icons/image-icon.svg" alt="image" className="w-4 h-4" /> Share as Image
                  </button>

                  <button
                    onClick={shareAsPDF}
                    className="flex items-center w-full gap-2 px-3 py-2 mt-1 text-sm font-medium text-white transition-all duration-200 bg-gradient-to-r from-blue-400 to-blue-500 hover:from-blue-500 hover:to-blue-600"
                  >
                    <img src="/icons/pdf-icon.svg" alt="pdf" className="w-4 h-4" /> Share as PDF
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const SalesOrderNewItem = ({ setView, context }) => {
  const { user } = useAuth();
  const [orderDetails, setOrderDetails] = useState(null);
  const [salesOrderItems, setSalesOrderItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Print modal state
  const [printContent, setPrintContent] = useState("");
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);

  // Fetch order data
  // Fetch order data
  const fetchOrderData = useCallback(
    async (showSpinner = true) => {
      if (showSpinner) setLoading(true);
      try {
        const orderData = await api.getSalesOrderForEdit(context.id);

        if (orderData?.Table?.length > 0) {
          setOrderDetails({
            ...orderData.Table[0],
            Table2: orderData.Table2 || [],
            Table3: orderData.Table3 || [], // ✅ add Table3 safely
          });
        }
        setSalesOrderItems(orderData?.Table1 || []);
      } catch (err) {
        console.error(err);
        setError("Failed to load order data.");
      } finally {
        if (showSpinner) setLoading(false);
      }
    },
    [context.id]
  );


  useEffect(() => {
    fetchOrderData();
  }, [fetchOrderData]);

  const handleQuotation = async () => {
    try {
      const resp = await api.get(`GetHtmlPoQuotationAPIRev?PoId=${context.id}`);
      console.log("Quotation API Response:", resp);

      const content = resp?.Table?.[0]?.PoQuotationHtml ?? null;

      if (content) {
        setPrintContent(content);

        const curOrder = orderDetails
          ? { ...orderDetails, Poid: context.id }
          : { Poid: context.id };
        setCurrentOrder(curOrder);
        setShowPrintPreview(true);
      } else {
        alert("Quotation content not found.");
      }
    } catch (err) {
      console.error("Error fetching quotation:", err);
      alert("Failed to fetch quotation.");
    }
  };

  const handlePrintInvoice = async () => {
    try {

      const resp = await api.MobGetfinalizeOrder(context.id);


      const content =
        resp?.Table?.[0]?.PrintContent ??
        resp?.Table3?.[0]?.PrintContent ??
        resp?.MobGetfinalizeOrderjs?.Table?.[0]?.PrintContent ??
        resp?.data?.MobGetfinalizeOrderjs?.Table?.[0]?.PrintContent ??
        null;

      if (content) {
        setPrintContent(content);

        const curOrder = orderDetails
          ? { ...orderDetails, Poid: context.id }
          : { Poid: context.id };
        setCurrentOrder(curOrder);
        setShowPrintPreview(true);


        try {
          const match = content.match(/Bill No\s*-?\s*([^*<\r\n]+)/i);
          const newInvNo = match ? match[1].trim() : null;
          if (newInvNo) {
            setOrderDetails((prev) => (prev ? { ...prev, InvNo: newInvNo } : prev));

          }
        } catch (ex) {
          // ignore parse errors
          console.warn("Invoice number parse error", ex);
        }
      } else {
        alert("Print content not found.");
      }
    } catch (err) {
      console.error(err);
      alert("Error fetching print content.");
    }
  };

  const triggerInvoiceGeneration = () => {
    const curOrder = orderDetails || {};
    if (!curOrder.InvNo || curOrder.InvNo === "InvNo :") {
      if (window.confirm("Invoice not generated yet. Are you really want to generate Invoice?")) {
        handlePrintInvoice();
      }
    } else {
      handlePrintInvoice();
    }
  };

  // --- F10 Shortcut for Quotation ---
  useEffect(() => {
    const handleF10 = (e) => {
      if (e.key === "F10") {
        e.preventDefault();
        handleQuotation();
      }
    };

    window.addEventListener("keydown", handleF10);
    return () => window.removeEventListener("keydown", handleF10);
  }, [orderDetails]); // depend on current order if needed


  useEffect(() => {
    const handleF4 = (e) => {
      if (e.key === "F4") {
        e.preventDefault();
        triggerInvoiceGeneration(); // ✅ now shows confirm alert
      }
    };

    window.addEventListener("keydown", handleF4);
    return () => window.removeEventListener("keydown", handleF4);
  }, [orderDetails]);


  useEffect(() => {
    const handleF7 = (e) => {
      if (e.key === "F7") {
        e.preventDefault();
        setView("salesorders");
      }
    };

    window.addEventListener("keydown", handleF7);
    return () => window.removeEventListener("keydown", handleF7);
  }, [setView]);

  if (loading) return <Spinner />;
  if (error) return <MessageBox message={error} />;

  return (
    <div className="w-full h-full m-0 p-0 bg-white">
      <div className="w-full h-full m-0 p-0">
        <div className="p-2">
          {/* Title */}
          <h1 className="text-xl font-bold text-center">
            Add Items to Sales Order #{context.id}/ {context.customer.Vname}
          </h1>

          {success && (
            <MessageBox message={success} type="success" onDismiss={() => setSuccess("")} />
          )}

          <OrderSalesOrderItems
            orderId={context.id}
            customerId={context.customerId}
            orderDate={context.orderDate}
            items={salesOrderItems}
            user={user}
            setSuccess={setSuccess}
            setError={setError}
            refetchData={() => fetchOrderData(false)}
          />

          {/* Footer buttons */}
          <div className="flex justify-between items-center pt-4 mt-4 border-t">
            <div className="flex gap-3">
              {/* ✅ New Quotation Button */}
              {/* <Button
  type="button"
  onClick={handleQuotation}
  className="bg-green-500 hover:bg-green-600 px-3 py-1 text-sm"
>
  Quotation (F10)
</Button> */}
              {/* Invoice Button - triggers API, preview modal */}
              <Button onClick={triggerInvoiceGeneration}>
                {orderDetails?.InvNo && orderDetails.InvNo !== "InvNo :"
                  ? orderDetails.InvNo
                  : "Generate Invoice(F4)"}
              </Button>
            </div>

            {/* Finish Sales Order Button */}
            <Button
              type="button"
              onClick={() => setView("sales")}
              className="bg-blue-500 hover:bg-blue-600 px-3 py-1 text-sm"
            >
              Finish Sales Order
            </Button>
          </div>
        </div>
      </div>

      {/* PrintPreview modal */}
      {showPrintPreview && printContent && (
        <PrintPreview
          htmlContent={printContent}
          onClose={() => {
            setShowPrintPreview(false);
            setPrintContent("");
            setCurrentOrder(null);
          }}
          order={currentOrder}
        />
      )}
    </div>
  );
};

export default SalesOrderNewItem;



// import React, { useState, useEffect, useCallback, useRef } from "react";
// import { useAuth } from "../../context/AuthContext";
// import api from "../../api/api";
// import html2canvas from "html2canvas";
// import { jsPDF } from "jspdf";
// import Spinner from "../../components/ui/Spinner";
// import MessageBox from "../../components/ui/MessageBox";
// import Button from "../../components/ui/Button";
// import SalesOrderItems from "./SalesOrderItems";
// import { XCircle, Printer, Send } from "lucide-react";

// /**
//  * PrintPreview component (self-contained here so SalesOrderNewItem works out of the box)
//  * props:
//  *  - htmlContent: string (HTML for preview)
//  *  - onClose: fn
//  *  - order: object (used for mobile number)
//  */
// const PrintPreview = ({ htmlContent, onClose, order }) => {
//   const hiddenRef = useRef(null);
//   const [waMenuOpen, setWaMenuOpen] = useState(false); // <-- added

//   useEffect(() => {
//     const handleKeyDown = async (e) => {
//       if (e.key === "Escape") {
//         onClose();
//       } else if (e.key === "F8") {
//         e.preventDefault();
//         if (!hiddenRef.current) return;

//         const mobileNumber = (order?.MobileNo || "").replace(/[^0-9]/g, "");
//         if (!mobileNumber) return;

//         const fullNumber = "91" + mobileNumber;

//         try {
//           const canvas = await html2canvas(hiddenRef.current, { scale: 2, useCORS: true });
//           const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
//           if (blob && navigator.clipboard && window.ClipboardItem) {
//             await navigator.clipboard.write([new window.ClipboardItem({ "image/png": blob })]);
//           }
//           const message = encodeURIComponent("");
//           window.open(`https://wa.me/${fullNumber}?text=${message}`, "_blank");
//         } catch (err) {
//           console.error("Failed to open WhatsApp:", err);
//         }
//       } else if (e.key === "F6") {
//         e.preventDefault();
//         handlePrint();
//       }
//     };

//     window.addEventListener("keydown", handleKeyDown);
//     return () => window.removeEventListener("keydown", handleKeyDown);
//   }, [order]);


//    const handlePrint = () => {
//     const iframe = document.getElementById('print-iframe');
//     if (iframe) {
//       iframe.contentWindow.focus();
//       iframe.contentWindow.print();
//     }
//   };

//   const handleWhatsAppClick = () => {
//     setWaMenuOpen((prev) => !prev);
//   };

//   const shareAsImage = async () => {
//     setWaMenuOpen(false);
//     if (!hiddenRef.current) return alert("Content not ready.");

//     const countryCode = "91";
//     const mobileNumber = order?.MobileNo?.replace(/[^0-9]/g, "") || "";
//     if (!mobileNumber) return alert("Mobile number not available.");

//     const fullNumber = countryCode + mobileNumber;

//     try {
//       const canvas = await html2canvas(hiddenRef.current, {
//         scale: 2,
//         useCORS: true,
//         backgroundColor: null,
//       });
//       const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
//       if (blob) await navigator.clipboard.write([new window.ClipboardItem({ "image/png": blob })]);

//       alert("Image copied! Paste in WhatsApp.");
//       window.open(`https://wa.me/${fullNumber}`, "_blank");
//     } catch (err) {
//       console.warn(err);
//     }
//   };

//   const shareAsPDF = async () => {
//     setWaMenuOpen(false);
//     if (!hiddenRef.current) return alert("Content not ready.");

//     try {
//       const canvas = await html2canvas(hiddenRef.current, { scale: 2, useCORS: true });
//       const doc = new jsPDF("p", "mm", "a4");
//       const imgData = canvas.toDataURL("image/png");

//       const pdfWidth = doc.internal.pageSize.getWidth();
//       const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
//       doc.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);

//       const pdfBlob = doc.output("blob");
//       const file = new File([pdfBlob], "invoice.pdf", { type: "application/pdf" });

//       if (navigator.canShare && navigator.canShare({ files: [file] })) {
//         await navigator.share({
//           files: [file],
//           title: "Invoice PDF",
//           text: "Please find attached invoice PDF",
//         });
//       } else {
//         const blobUrl = URL.createObjectURL(pdfBlob);
//         alert("Your browser cannot share files directly. Please download the PDF and send via WhatsApp.");
//         window.open(blobUrl, "_blank");
//         setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
//       }
//     } catch (err) {
//       console.error(err);
//     }
//   };

//   return (
//     <>
//       {/* Hidden content for HTML to Canvas */}
//       <div
//         ref={hiddenRef}
//         style={{ position: "fixed", top: "-9999px", left: "-9999px", width: "500px", background: "#fff", padding: "10px" }}
//         dangerouslySetInnerHTML={{ __html: htmlContent }}
//       />

//       {/* Modal */}
//        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
//          <div className="relative w-full max-w-sm p-4 bg-white rounded-lg shadow-2xl">
//          <h3 className="mb-2 text-lg font-semibold text-center">Preview</h3>

//           <div className="border border-gray-300">
//             <iframe
//               id="print-iframe"
//               srcDoc={htmlContent}
//               title="Print Preview"
//               style={{ width: '100%', height: '60vh', border: 'none' }}
//             />
//           </div>


//           {/* Footer Buttons */}
//           <div className="flex justify-end mt-2 gap-7">
//             <Button
//               onClick={onClose}
//               className="flex items-center gap-2 px-2 py-1.5 text-sm font-medium text-white
//             bg-gradient-to-r from-red-500 to-red-600 rounded-lg shadow-md
//             hover:from-red-600 hover:to-red-700 hover:shadow-lg
//             transition-all duration-300 ease-in-out transform hover:scale-105"
//           >
//               <XCircle size={16} /> Close
//             </Button>

//             <Button
//               onClick={handlePrint}
//               className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white
//                bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg shadow-md
//                hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg
//                transition-all duration-300 ease-in-out transform hover:scale-105"
//             >
//               <Printer size={16} /> Print (F6)
//             </Button>

//             {/* WhatsApp Button */}
//             <div className="relative">
//               <Button
//                 onClick={handleWhatsAppClick}
//                 className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white
//       bg-green-500 rounded-lg shadow-md hover:bg-green-600 transition-all duration-300"
//   >
//                 <Send size={16} /> WhatsApp
//               </Button>

//               {waMenuOpen && (
//                 <div className="absolute right-0 z-50 mt-2 overflow-hidden bg-white border rounded-lg shadow-lg w-44">
//                   <button
//                     onClick={shareAsImage}
//                     className="flex items-center w-full gap-2 px-3 py-2 text-sm font-medium text-white transition-all duration-200 bg-gradient-to-r from-green-400 to-green-500 hover:from-green-500 hover:to-green-600"
//                   >
//                     <img src="/icons/image-icon.svg" alt="image" className="w-4 h-4" /> Share as Image
//                   </button>

//                   <button
//                     onClick={shareAsPDF}
//                     className="flex items-center w-full gap-2 px-3 py-2 mt-1 text-sm font-medium text-white transition-all duration-200 bg-gradient-to-r from-blue-400 to-blue-500 hover:from-blue-500 hover:to-blue-600"
//                   >
//                     <img src="/icons/pdf-icon.svg" alt="pdf" className="w-4 h-4" /> Share as PDF
//                   </button>
//                 </div>
//               )}
//             </div>
//           </div>
//         </div>
//       </div>
//     </>
//   );
// };

// const SalesOrderNewItem = ({ setView, context }) => {
//   const { user } = useAuth();
//   const [orderDetails, setOrderDetails] = useState(null);
//   const [salesOrderItems, setSalesOrderItems] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState("");
//   const [success, setSuccess] = useState("");

//   // Print modal state
//   const [printContent, setPrintContent] = useState("");
//   const [showPrintPreview, setShowPrintPreview] = useState(false);
//   const [currentOrder, setCurrentOrder] = useState(null);

//   // Fetch order data
//   // Fetch order data
//   const fetchOrderData = useCallback(
//     async (showSpinner = true) => {
//       if (showSpinner) setLoading(true);
//       try {
//         const orderData = await api.getSalesOrderForEdit(context.id);

//         if (orderData?.Table?.length > 0) {
//           setOrderDetails({
//             ...orderData.Table[0],
//             Table2: orderData.Table2 || [],
//             Table3: orderData.Table3 || [], // ✅ add Table3 safely
//           });
//         }
//         setSalesOrderItems(orderData?.Table1 || []);
//       } catch (err) {
//         console.error(err);
//         setError("Failed to load order data.");
//       } finally {
//         if (showSpinner) setLoading(false);
//       }
//     },
//     [context.id]
//   );


//   useEffect(() => {
//     fetchOrderData();
//   }, [fetchOrderData]);


//   const handlePrintInvoice = async () => {
//     try {

//       const resp = await api.MobGetfinalizeOrder(context.id);


//       const content =
//         resp?.Table?.[0]?.PrintContent ??
//         resp?.Table3?.[0]?.PrintContent ??
//         resp?.MobGetfinalizeOrderjs?.Table?.[0]?.PrintContent ??
//         resp?.data?.MobGetfinalizeOrderjs?.Table?.[0]?.PrintContent ??
//         null;

//       if (content) {
//         setPrintContent(content);

//         const curOrder = orderDetails
//           ? { ...orderDetails, Poid: context.id }
//           : { Poid: context.id };
//         setCurrentOrder(curOrder);
//         setShowPrintPreview(true);


//         try {
//           const match = content.match(/Bill No\s*-?\s*([^*<\r\n]+)/i);
//           const newInvNo = match ? match[1].trim() : null;
//           if (newInvNo) {
//             setOrderDetails((prev) => (prev ? { ...prev, InvNo: newInvNo } : prev));

//           }
//         } catch (ex) {
//           // ignore parse errors
//           console.warn("Invoice number parse error", ex);
//         }
//       } else {
//         alert("Print content not found.");
//       }
//     } catch (err) {
//       console.error(err);
//       alert("Error fetching print content.");
//     }
//   };

//   useEffect(() => {
//     const handleF4 = (e) => {
//       if (e.key === "F4") {
//         e.preventDefault();
//         handlePrintInvoice();
//       }
//     };

//     window.addEventListener("keydown", handleF4);
//     return () => window.removeEventListener("keydown", handleF4);
//   }, [handlePrintInvoice]);

//   useEffect(() => {
//     const handleF7 = (e) => {
//       if (e.key === "F7") {
//         e.preventDefault();
//         setView("salesorders");
//       }
//     };

//     window.addEventListener("keydown", handleF7);
//     return () => window.removeEventListener("keydown", handleF7);
//   }, [setView]);

//   if (loading) return <Spinner />;
//   if (error) return <MessageBox message={error} />;

//   return (
//     <div className="w-full h-full m-0 p-0 bg-white">
//       <div className="w-full h-full m-0 p-0">
//         <div className="p-2">
//           {/* Title */}
//           <h1 className="text-xl font-bold text-center">
//             Add Items to Sales Order #{context.id}/ {context.customer.Vname}
//           </h1>

//           {success && (
//             <MessageBox message={success} type="success" onDismiss={() => setSuccess("")} />
//           )}

//           <SalesOrderItems
//             orderId={context.id}
//             customerId={context.customerId}
//             orderDate={context.orderDate}
//             items={salesOrderItems}
//             user={user}
//             setSuccess={setSuccess}
//             setError={setError}
//             refetchData={() => fetchOrderData(false)}
//           />

//           {/* Footer buttons */}
//           <div className="flex justify-between items-center pt-4 mt-4 border-t">
//             {/* Invoice Button - triggers API, preview modal */}
//             <Button
//               type="button"
//               onClick={handlePrintInvoice}
//               className="bg-green-500 hover:bg-green-600 px-3 py-1 text-sm"
//             >
//               Generate Invoice (F4)
//             </Button>

//             {/* Finish Sales Order Button */}
//             <Button
//               type="button"
//               onClick={() => setView("salesorders")}
//               className="bg-blue-500 hover:bg-blue-600 px-3 py-1 text-sm"
//             >
//               Finish Sales Order
//             </Button>
//           </div>
//         </div>
//       </div>

//       {/* PrintPreview modal */}
//       {showPrintPreview && printContent && (
//         <PrintPreview
//           htmlContent={printContent}
//           onClose={() => {
//             setShowPrintPreview(false);
//             setPrintContent("");
//             setCurrentOrder(null);
//           }}
//           order={currentOrder}
//         />
//       )}
//     </div>
//   );
// };

// export default SalesOrderNewItem;








